import type { NuxtError } from '#app'
import type { Order } from '@brighterly/lib-core-types/orders/interfaces'

import { useToast } from '@shared/lib/toast'
import { useCsRoutes } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { show404 } from '@shared/lib/page-error'

export interface OrderApiOptions {
  onStart?: () => void
  onSuccess?: (order: Order) => void
  onError?: () => void
  onFinish?: () => void
}

export interface OrderApiPayload {
  orderId: number
  solidOrderId?: string | null
  paymentIntentId?: string | null
}

export const useOrderApi = async (payload: OrderApiPayload, options?: OrderApiOptions): Promise<void | NuxtError> => {
  const { $axios } = useNuxtApp()

  const toast = useToast()

  const url = useCsRoutes('orders.show', pick({
    '[order_id]': payload.orderId,
    solid_order_id: payload.solidOrderId || null,
    payment_intent: payload.paymentIntentId || null,
  }))

  const set404 = (): NuxtError => {
    options?.onError?.()
    return show404()
  }

  if (!url) return set404()
  options?.onStart?.()

  try {
    const { data } = await $axios.get(url)
    if (!data.success) return set404()

    const order: Order | null = data?.data || null
    if (!order) return set404()

    options?.onSuccess?.(order)
  } catch (err: any) {
    const is404 = err?.response?.status === 404
    if (is404) return set404()

    options?.onError?.()
    toast.error(err?.response?.data?.message || err?.message || 'Unexpected error')
  } finally {
    options?.onFinish?.()
  }
}
